import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, Clock } from 'lucide-react';
import { attendanceApi, employeeApi, locationApi, companySettingsApi, scheduleApi } from '../services/api';
import { AttendanceRecord } from '../types';
import { useAuth } from '../context/AuthContext';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import ClockInModal from '../components/ClockInModal';
import FaceEnrollmentModal from '../components/FaceEnrollmentModal';
import ClockSuccessDialog from '../components/ClockSuccessDialog';
import InactiveLocationBanner from '../components/InactiveLocationBanner';
import { WeekNavigator } from '../components/WeekNavigator';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { appConfirm } from '../context/SystemDialogContext';
import { parseLocalDate } from '../utils/payrollPeriodLocal';
import { formatCompanyDate } from '../utils/companyDateFormat';
import { getLocationDisplayName, getLocationOptionStyle, isLocationActive } from '../utils/locationDisplay';
import {
  compareWallClock,
  extractDatePart,
  formatDisplayTime,
  wallClockDiffHours,
} from '../utils/datetime';

interface LocationOption {
  id: number;
  name: string;
  isActive?: boolean;
}

interface ScheduleOption {
  id: number;
  name: string;
}

const MyAttendances: React.FC = () => {
  const { employee, updateEmployee } = useAuth();
  const weekStartsOn = useWeekStartDay();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingClockOut, setPendingClockOut] = useState<AttendanceRecord | null>(null);
  const [clockActionLoading, setClockActionLoading] = useState(false);
  const [showClockModal, setShowClockModal] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [assignedSchedules, setAssignedSchedules] = useState<ScheduleOption[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [mandatoryScheduleInputEnabled, setMandatoryScheduleInputEnabled] = useState(false);
  const [allowFaceBypass, setAllowFaceBypass] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successAction, setSuccessAction] = useState<'clockIn' | 'clockOut'>('clockIn');
  const [showFaceEnrollmentModal, setShowFaceEnrollmentModal] = useState(false);
  const [postEnrollmentImage, setPostEnrollmentImage] = useState<string | null>(null);
  const [isPostEnrollmentAttendance, setIsPostEnrollmentAttendance] = useState(false);

  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const selectedLocation = useMemo(
    () => (selectedLocationId != null ? locations.find((l) => l.id === selectedLocationId) ?? null : null),
    [locations, selectedLocationId]
  );
  const isSelectedLocationInactive = selectedLocation != null && !isLocationActive(selectedLocation);
  const firstActiveLocation = useMemo(
    () => locations.find((loc) => isLocationActive(loc)) ?? null,
    [locations]
  );

  const fetchAttendanceRecords = async () => {
    if (!employee?.id) return;
    try {
      setLoading(true);
      const startDateStr = format(weekStart, 'yyyy-MM-dd');
      const endDateStr = format(weekEnd, 'yyyy-MM-dd');
      const records = await attendanceApi.getAll({
        startDate: startDateStr,
        endDate: endDateStr,
        employeeId: employee.id,
      });
      const sortedRecords = [...records].sort((a, b) => compareWallClock(a.clockInTime, b.clockInTime));
      setAttendanceRecords(sortedRecords);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const checkPendingClockOut = async () => {
    if (!employee?.id) return;
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const records = await attendanceApi.getAll({
        startDate: today,
        endDate: today,
        employeeId: employee.id,
      });
      const latestWithoutClockOut = records
        .filter(r => !r.clockOutTime)
        .sort((a, b) => compareWallClock(b.clockInTime, a.clockInTime))[0];
      setPendingClockOut(latestWithoutClockOut || null);
    } catch (error) {
      console.error('Error checking pending clock out:', error);
      setPendingClockOut(null);
    }
  };

  const fetchEmployeeLocations = async () => {
    if (!employee?.id) return;
    try {
      const emp = await employeeApi.getById(employee.id);
      const allLocs = await locationApi.getAll();
      const mainId = emp.profile?.mainLocationId;
      const subIds = emp.profile?.subLocationIds || [];
      const locIds = [...(mainId ? [mainId] : []), ...subIds.filter((id: number) => id !== mainId)];
      const locs: LocationOption[] = locIds.flatMap((id: number) => {
        const loc = allLocs.find((l) => l.id === id);
        return loc ? [{ id: loc.id, name: loc.name, isActive: loc.isActive }] : [];
      });
      setLocations(locs);
      if (locs.length > 0 && !selectedLocationId) {
        setSelectedLocationId(locs[0].id);
      }

      const scheduleIds = emp.profile?.scheduleIds || [];
      if (scheduleIds.length > 0) {
        const allSchedules = await scheduleApi.getAll();
        const scheds: ScheduleOption[] = scheduleIds.flatMap((id: number) => {
          const sched = allSchedules.find((s) => s.id === id);
          return sched ? [{ id: sched.id, name: sched.name }] : [];
        });
        setAssignedSchedules(scheds);
        if (scheds.length === 1) {
          setSelectedScheduleId(scheds[0].id);
        } else if (scheds.length > 1 && !selectedScheduleId) {
          setSelectedScheduleId(scheds[0].id);
        }
      } else {
        setAssignedSchedules([]);
        setSelectedScheduleId(null);
      }
    } catch (e) {
      console.error('Error fetching employee locations:', e);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const settings = await companySettingsApi.get();
      setAllowFaceBypass(settings?.allowMobileClockInOutWithoutFaceVerification ?? false);
      setMandatoryScheduleInputEnabled(settings?.mandatoryScheduleInputEnabled ?? false);
    } catch {
      setAllowFaceBypass(false);
      setMandatoryScheduleInputEnabled(false);
    }
  };

  useEffect(() => {
    fetchAttendanceRecords();
  }, [weekStart, weekEnd, employee?.id]);

  useEffect(() => {
    checkPendingClockOut();
  }, [employee?.id]);

  useEffect(() => {
    fetchEmployeeLocations();
    fetchCompanySettings();
  }, [employee?.id]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchAttendanceRecords();
    checkPendingClockOut();
  };

  const handleOpenClockModal = async () => {
    if (locations.length === 0) {
      alert('No location assigned to your profile. Please contact your administrator.');
      return;
    }

    const activeLocation = firstActiveLocation;
    if (!activeLocation) {
      alert('All of your assigned locations are inactive. Clock in/out is not available.');
      return;
    }

    if (!allowFaceBypass && employee?.id) {
      try {
        const profile = await employeeApi.getById(employee.id);
        const hasFaceEnrolled = !!(profile?.faceId && String(profile.faceId).trim() !== '');
        if (!hasFaceEnrolled) {
          const shouldEnroll = await appConfirm(
            'You have not enrolled your face yet. You must enroll your face before you can clock in or out.\n\nWould you like to enroll now?',
            { confirmLabel: 'Enroll' }
          );
          if (shouldEnroll) {
            setShowFaceEnrollmentModal(true);
          }
          return;
        }
      } catch (error) {
        console.error('Error checking face enrollment:', error);
        alert('Could not verify face enrollment. Please try again.');
        return;
      }
    }

    if (!pendingClockOut && locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(activeLocation.id);
    }
    if (!pendingClockOut && isSelectedLocationInactive) {
      setSelectedLocationId(activeLocation.id);
    }
    setShowClockModal(true);
  };

  const handleFaceEnrollmentSuccess = async (imageBase64: string) => {
    if (!employee?.id) return;

    try {
      const refreshed = await employeeApi.getById(employee.id);
      updateEmployee({ ...employee, ...refreshed });
    } catch {
      updateEmployee({ ...employee, faceId: employee.faceId || 'enrolled' });
    }

    setPostEnrollmentImage(imageBase64);
    setIsPostEnrollmentAttendance(true);
    if (!pendingClockOut && locations.length > 0 && !selectedLocationId && firstActiveLocation) {
      setSelectedLocationId(firstActiveLocation.id);
    }
    setShowClockModal(true);
  };

  const handleCloseClockModal = () => {
    setShowClockModal(false);
    setPostEnrollmentImage(null);
    setIsPostEnrollmentAttendance(false);
  };

  const handleClockSubmit = async (imageBase64: string, locationId: number, skipFaceVerification: boolean, scheduleId?: number) => {
    if (!employee?.id) return;
    const targetLocation = locations.find((l) => l.id === locationId);
    if (targetLocation && !isLocationActive(targetLocation)) {
      alert('This location is inactive. Clock in/out is not allowed.');
      return;
    }
    if (pendingClockOut) {
      const locId = pendingClockOut.locationId ?? locationId;
      await attendanceApi.clockOut(employee.id, imageBase64, undefined, undefined, locId, skipFaceVerification);
      setSuccessAction('clockOut');
      setShowSuccessDialog(true);
    } else {
      await attendanceApi.clockIn(
        employee.id,
        imageBase64,
        undefined,
        undefined,
        locationId,
        skipFaceVerification,
        scheduleId
      );
      setSuccessAction('clockIn');
      setShowSuccessDialog(true);
    }
    await fetchAttendanceRecords();
    await checkPendingClockOut();
  };

  const getAttendanceForDay = (date: Date): AttendanceRecord[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return attendanceRecords.filter(record => extractDatePart(record.clockInTime) === dateStr);
  };

  const formatTime = (dateTimeStr: string): string => formatDisplayTime(dateTimeStr);

  const calculateTotalHours = (record: AttendanceRecord): number => {
    if (!record.clockInTime || !record.clockOutTime) return 0;
    return wallClockDiffHours(record.clockInTime, record.clockOutTime);
  };

  const totalRecords = attendanceRecords.length;
  const totalHours = attendanceRecords
    .filter(r => r.clockInTime && r.clockOutTime)
    .reduce((sum, record) => sum + calculateTotalHours(record), 0);
  const daysWithAttendance = new Set(
    attendanceRecords.map(r => extractDatePart(r.clockInTime)).filter(Boolean)
  ).size;

  return (
    <div className="p-6 space-y-5 max-w-2xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold text-neutral-800">My Attendances</h1>
      </div>

      {/* Clock In/Out Button */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 flex flex-col items-center justify-center gap-2">
        {isSelectedLocationInactive && selectedLocation && (
          <InactiveLocationBanner locationName={selectedLocation.name} className="w-full mb-0" />
        )}
        <button
          onClick={handleOpenClockModal}
          disabled={clockActionLoading || locations.length === 0 || locations.every((loc) => !isLocationActive(loc))}
          className={`w-full max-w-[200px] flex items-center justify-center gap-2 py-3 px-5 rounded-lg font-semibold text-base transition-colors disabled:opacity-50 ${
            pendingClockOut
              ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-green-500 hover:bg-green-600 text-white'
          }`}
        >
          {clockActionLoading ? (
            <>
              <RefreshCw className="h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Clock className="h-5 w-5" />
              {pendingClockOut ? 'Clock Out' : 'Clock In'}
            </>
          )}
        </button>
        {pendingClockOut && (
          <p className="mt-2 text-sm text-neutral-500 text-center">
            You are currently clocked in since {formatTime(pendingClockOut.clockInTime)}
          </p>
        )}
      </div>

      {/* Week Navigator */}
      <div className="flex flex-wrap items-center justify-center gap-3 bg-white rounded-lg shadow-sm border border-neutral-200 p-3">
        <WeekNavigator
          variant="neutral"
          weekStart={weekStart}
          weekEnd={weekEnd}
          weekStartsOn={weekStartsOn}
          currentWeek={currentWeek}
          onPreviousWeek={() => setCurrentWeek(subWeeks(currentWeek, 1))}
          onNextWeek={() => setCurrentWeek(addWeeks(currentWeek, 1))}
          onCurrentWeek={() => setCurrentWeek(new Date())}
          onWeekSelect={(ymd) => setCurrentWeek(parseLocalDate(ymd))}
          currentWeekLabel="This Week"
          endContent={
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 transition-colors disabled:opacity-50"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          }
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading attendances...</div>
        </div>
      ) : (
        <>
          {/* Summary Section */}
          <div className="flex gap-3">
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-neutral-200 p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{totalRecords}</div>
              <div className="text-sm text-neutral-500">Total Records</div>
            </div>
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-neutral-200 p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{totalHours.toFixed(1)}h</div>
              <div className="text-sm text-neutral-500">Total Hours</div>
            </div>
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-neutral-200 p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">{daysWithAttendance}</div>
              <div className="text-sm text-neutral-500">Days</div>
            </div>
          </div>

          {/* Daily Attendance */}
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
            <div className="px-3 py-2.5 border-b border-neutral-200 bg-neutral-50">
              <h2 className="text-base font-semibold text-neutral-800">Daily Attendance</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">In</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Out</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {weekDays.map(day => {
                    const dayRecords = getAttendanceForDay(day);
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    if (dayRecords.length === 0) return null;
                    return (
                      <React.Fragment key={day.toISOString()}>
                        <tr className="bg-neutral-50">
                          <td colSpan={3} className="px-3 py-1.5 text-sm font-semibold text-neutral-700">
                            {format(day, 'EEEE')}, {formatCompanyDate(day)}
                            {isToday && <span className="ml-2 text-blue-600">(Today)</span>}
                          </td>
                        </tr>
                        {dayRecords.map(record => (
                          <tr key={record.id} className="hover:bg-neutral-50">
                            <td className="px-3 py-2 text-sm font-medium text-neutral-900">{formatTime(record.clockInTime)}</td>
                            <td className="px-3 py-2 text-sm text-neutral-700">
                              {record.clockOutTime ? formatTime(record.clockOutTime) : <span className="text-amber-600 italic">Pending</span>}
                            </td>
                            <td className="px-3 py-2 text-sm font-medium text-blue-600">
                              {record.clockOutTime ? `${calculateTotalHours(record).toFixed(2)}h` : '-'}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {attendanceRecords.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-neutral-500">
                        No attendance records for this week.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ClockInModal
        isOpen={showClockModal}
        onClose={handleCloseClockModal}
        onSuccess={() => {}}
        action={pendingClockOut ? 'clockOut' : 'clockIn'}
        employeeId={employee?.id ?? 0}
        locations={locations}
        selectedLocationId={pendingClockOut ? (pendingClockOut.locationId ?? selectedLocationId ?? locations[0]?.id ?? null) : selectedLocationId}
        onLocationChange={setSelectedLocationId}
        schedules={assignedSchedules}
        selectedScheduleId={selectedScheduleId}
        onScheduleChange={setSelectedScheduleId}
        requireScheduleSelection={mandatoryScheduleInputEnabled}
        onSubmit={handleClockSubmit}
        allowFaceBypass={allowFaceBypass}
        prefilledImageBase64={postEnrollmentImage}
        prefilledVerified={isPostEnrollmentAttendance}
        skipFaceVerificationOnSubmit={isPostEnrollmentAttendance}
      />

      <FaceEnrollmentModal
        isOpen={showFaceEnrollmentModal}
        onClose={() => setShowFaceEnrollmentModal(false)}
        employeeId={employee?.id ?? 0}
        onSuccess={handleFaceEnrollmentSuccess}
      />

      <ClockSuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        action={successAction}
      />
    </div>
  );
};

export default MyAttendances;
